<template>
  <Dialog
    v-model:visible="visibleModel"
    modal
    :header="dialogHeader"
    class="log-detail-dialog"
    :style="{ width: 'min(96vw, 1100px)' }"
    :breakpoints="{ '960px': '95vw' }"
    @hide="$emit('close')"
  >
    <div v-if="loading" class="dialog-loading">
      <ProgressSpinner style="width: 40px; height: 40px" />
    </div>
    <div v-else-if="log" class="log-detail-layout">
      <div class="log-meta-panel">
        <dl class="meta-list">
          <dt>ID</dt><dd>{{ log.id }}</dd>
          <dt>Created</dt><dd>{{ formatDate(log.created_at) }}</dd>
          <dt>User</dt><dd>{{ log.user_id || '—' }}</dd>
          <dt>Session</dt>
          <dd>
            <router-link
              v-if="log.session_id"
              :to="`/rag-monitor/sessions/${log.session_id}`"
              @click="visibleModel = false"
            >
              {{ log.session_id }}
            </router-link>
            <span v-else>—</span>
          </dd>
          <dt>Mode</dt><dd>{{ log.mode || '—' }}</dd>
          <dt>Chunks</dt><dd>{{ log.chunks_count ?? '—' }}</dd>
          <dt>Latency</dt><dd>{{ log.latency_ms != null ? `${log.latency_ms} ms` : '—' }}</dd>
          <dt>Tokens</dt><dd>{{ log.tokens_used ?? '—' }}</dd>
        </dl>
        <section class="query-block">
          <h4>Query</h4>
          <pre class="mono-block">{{ log.query }}</pre>
        </section>
        <section class="response-block">
          <h4>Response preview</h4>
          <pre class="mono-block">{{ log.response_preview || '—' }}</pre>
        </section>
      </div>
      <div v-if="session?.messages" class="log-chat-panel">
        <h4>Session messages</h4>
        <ChatThread
          :messages="session.messages"
          :user-id="session.user_id"
          :session-id="session.id"
          :updated-at="session.updated_at"
        />
      </div>
    </div>
    <p v-else-if="error" class="dialog-error">{{ error }}</p>
  </Dialog>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import ChatThread from './ChatThread.vue';
import { getExecutionLogById } from '@/services/ragMonitorApi';

const props = defineProps({
  visible: { type: Boolean, default: false },
  logId: { type: [Number, String], default: null }
});

const emit = defineEmits(['update:visible', 'close']);

const visibleModel = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v)
});

const loading = ref(false);
const error = ref('');
const log = ref(null);
const session = ref(null);

const dialogHeader = computed(() =>
  log.value ? `Execution log #${log.value.id}` : 'Execution log'
);

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('ru-RU');
}

async function loadDetail() {
  if (!props.logId || !props.visible) return;
  loading.value = true;
  error.value = '';
  log.value = null;
  session.value = null;
  try {
    const data = await getExecutionLogById(props.logId);
    log.value = data.log;
    session.value = data.session;
  } catch (e) {
    error.value = e.message || 'Ошибка загрузки';
  } finally {
    loading.value = false;
  }
}

watch(
  () => [props.visible, props.logId],
  () => {
    if (props.visible && props.logId) loadDetail();
  },
  { immediate: true }
);
</script>

<style scoped>
.log-detail-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  max-height: 75vh;
}

@media (max-width: 768px) {
  .log-detail-layout {
    grid-template-columns: 1fr;
    max-height: none;
  }
}

.log-meta-panel {
  overflow-y: auto;
  max-height: 75vh;
}

.log-chat-panel {
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.log-chat-panel h4 {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
}

.meta-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 1rem;
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.meta-list dt {
  font-weight: 600;
  opacity: 0.7;
}

.meta-list dd {
  margin: 0;
  word-break: break-all;
}

.query-block,
.response-block {
  margin-top: 1rem;
}

.query-block h4,
.response-block h4 {
  margin: 0 0 0.35rem;
  font-size: 0.85rem;
}

.mono-block {
  margin: 0;
  padding: 0.65rem;
  background: var(--p-content-hover-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 6px;
  color: var(--p-text-color);
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.82rem;
  max-height: 200px;
  overflow-y: auto;
}

.dialog-loading {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.dialog-error {
  color: var(--p-red-500, #dc3545);
}
</style>
